import _sortBy from 'lodash/sortBy';
import _filter from 'lodash/filter';
import _map from 'lodash/map';
import _uniqBy from 'lodash/uniqBy';
import _isEmpty from 'lodash/isEmpty';
import {
  gqlRosterInvitationRequestStatus,
  gqlProfileStatusEnum as PROFILE_STATUS,
  gqlMediaTypeCodeEnum as MEDIA_TYPE,
  gqlMediaModerationStatusEnum as MEDIA_MODERATION_STATUS,
  gqlItemEnum as UPLOAD_ITEM,
  gqlArtist,
} from '@minecraft.graphql-types';
import { UserContextFragment, AccountArtistFragment, AccountProfileFragment } from '@minecraft.graphql-operations';
import appConfig from 'config/appConfig';
import moment from 'moment';
import { clearStorage, getAccessToken, parseJwt } from '@blocs.auth';
import { NormalizedProfile } from '../components/Profile/ProfileTypes';

export const isAdmin = () => {
  try {
    const jwt = parseJwt(getAccessToken());

    return jwt?.loginAttributes?.service === 'BACKLOT';
  } catch {
    clearStorage();
  }

  return false;
};

export const mediaMap = (media) => {
  const mediaList = {
    VIDEO: [],
  };
  // eslint-disable-next-line no-unused-expressions

  !_isEmpty(media) && mediaList[media.mediaType.code].push(media);

  return mediaList;
};

export const parseErrorResponse = (data) => {
  if (!Array.isArray(data?.errors) && !Array.isArray(data?.error?.graphQLErrors)) {
    return data?.message || 'An unknown error occurred';
  }

  const errorList = data?.errors || data?.error?.graphQLErrors || [];

  return errorList
    .map((e) => {
      if (e) {
        const errors = [];

        if (e?.data?.errorData) {
          errors.push(e?.data?.errorData?.message);
        }

        if (e?.data?.internalData?.internalMessage?.publicData) {
          errors.push(e?.data?.internalData?.internalMessage?.publicData);
        }

        if (errors && errors?.length) {
          return errors.join(', ');
        }

        return e.message;
      }

      return '';
    })
    .filter((e) => !!e);
};

export const isErrorExistsForErrorCode = (errors, errorCode) => {
  const errorData = errors.find((e) => e && e.data && e.data.name && e.data.name === errorCode);

  if (errorData) {
    const email = (errorData.data && errorData.data.internalData && errorData.data.internalData.email) || '';

    return {
      isErrorExists: true,
      email,
    };
  }

  return null;
};

export const formatGMTOffset = (offset) => {
  const minus = offset < 0;
  const absOffset = Math.abs(offset);
  const hours = parseInt(absOffset.toString(), 10);
  const minutes = (absOffset - hours) * 60;

  return `(GMT${minus ? '-' : '+'}${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes})`;
};

export const formatTimeZones = (timezones) =>
  _sortBy(
    timezones
      .filter((timeZone) => timeZone.enabled)
      .map((opt) => ({
        ...opt,
        label: `${formatGMTOffset(opt.gmtOffset)} ${opt.name}`,
        value: opt.id,
        key: opt.id,
      })),
    (item) => item.gmtOffset
  );

export function findProfileById(artists: AccountArtistFragment[], profileId: string): AccountProfileFragment {
  return artists?.reduce((acc, artist) => {
    if (acc) {
      return acc;
    }

    return artist?.profiles?.find((p) => p?.profileId?.toString() === profileId?.toString());
  }, null as AccountProfileFragment);
}

export function findArtistByProfileId<T extends gqlArtist | AccountArtistFragment>(artists: T[], profileId: string) {
  if (!Array.isArray(artists) || !profileId) {
    return null;
  }

  return artists.reduce((acc, artist) => {
    const profiles = artist?.profiles;

    if (Array.isArray(profiles) && (!acc || profiles.find((p) => p?.profileId?.toString() === profileId?.toString()))) {
      return artist;
    }

    return acc;
  }, null);
}

export function getSelectedArtist<T extends gqlArtist | AccountArtistFragment>(artists: T[], latestArtistId: number) {
  if (artists) {
    return artists.reduce((selected, current) => {
      if (selected) {
        if (selected.artistId === latestArtistId) {
          return selected;
        }

        if (current.artistId === latestArtistId || current.created < selected.created) {
          return current;
        }

        return selected;
      }

      return current;
    }, null);
  }

  return null;
}

/**
 * Returns the default profile for an account
 * The default is "personal" and "active"
 */
export function getDefaultProfile(artist: gqlArtist | AccountArtistFragment) {
  if (artist) {
    let profiles: AccountProfileFragment[] = (artist as AccountArtistFragment)?.profiles?.concat([]);
    const activeProfiles = profiles.filter((profile) => profile?.profileStatus?.code === PROFILE_STATUS.ACTIVE);

    if (activeProfiles.length) {
      profiles = activeProfiles;
    }

    return profiles.reduce((selected, current) => {
      if (selected) {
        return current.isPersonal ? current : selected;
      }

      return current;
    }, null);
  }

  return null;
}

export const getAgeInMonths = (isSwitchToMonths, age) => {
  const multiplyBy = isSwitchToMonths ? 1 : 12;

  return parseInt(age, 10) * multiplyBy;
};

export const getMediaTypeExtensions = (mediaType) => {
  return mediaType === MEDIA_TYPE.PHOTO ? appConfig.photoExtensions : null;
};

export function getActivePersonalProfile(artist) {
  if (artist) {
    return artist.profiles.filter((p) => p.isPersonal && p.profileStatus.code === PROFILE_STATUS.ACTIVE)[0];
  }

  return null;
}

type ErrMsgObj = { statusCode?: number; message?: string };

export function parseOrganizationData(response: {
  data?: {
    rosterTalentInvitationByToken?: {
      expiredAt: string;
      status: gqlRosterInvitationRequestStatus;
      organizationId?: number;
    };
  };
  error?: { graphQLErrors?: readonly { message?: string | ErrMsgObj }[] };
  errors?: readonly {
    message?: string | ErrMsgObj;
    data?: { errorData?: ErrMsgObj };
  }[];
}) {
  const tokenInfo = response?.data?.rosterTalentInvitationByToken;

  if (tokenInfo) {
    return {
      isExpired: moment(tokenInfo.expiredAt).isBefore(moment()),
      isCompleted: tokenInfo.status === gqlRosterInvitationRequestStatus.IN_ROSTER,
      organizationId: tokenInfo.organizationId,
      errorMessage: null,
    };
  }

  const message = response?.errors?.[0]?.message || response?.error?.graphQLErrors?.[0]?.message;
  let isExpired = (message as ErrMsgObj)?.statusCode === 400; // -- statusCode = 400 for expired token
  let errorMessage = (message as ErrMsgObj)?.statusCode === 404 && (message as ErrMsgObj).message; // -- statusCode =  404 for invalid token

  if (typeof message === 'string' && response?.errors?.[0]?.data?.errorData) {
    const errorStatusCode = response.errors[0].data.errorData.statusCode;

    isExpired = errorStatusCode === 400;
    errorMessage = errorStatusCode === 404 && message;
  }

  return {
    isExpired,
    isCompleted: false,
    organizationId: null,
    errorMessage,
  };
}

export function parseProfileInfo(profile: AccountProfileFragment): NormalizedProfile {
  let { stageName } = profile;
  const {
    profileId,
    isPersonal,
    deactivatedBy,
    stageName: profileStageName,
    profileVisiblities,
    profileStatus: { code },
    isSeekingRepresentation,
    seekingRepresentations,
    noteToRepresentative,
    photos,
  } = profile;
  let photo: NormalizedProfile['photo'] = Array.isArray(photos)
    ? photos?.filter((p) => p?.isPrimary)?.[0]?.media
    : null;
  const organization = !profile?.isPersonal && profile?.organizations ? profile?.organizations?.[0] : null;
  let isDivision = false;

  if (!profile?.isPersonal && organization) {
    isDivision = !profile?.isPersonal && !!organization?.parentOrganization;

    if (organization?.logo) {
      photo = organization.logo;
    } else if (isDivision && organization.parentOrganization.logo) {
      photo = organization.parentOrganization.logo;
    } else {
      photo = null;
    }

    stageName = isDivision ? organization.parentOrganization.name : organization.name;
  }

  return {
    profileId,
    photo,
    isPersonal,
    stageName,
    profileStageName,
    deactivatedBy,
    profileVisiblities,
    organizationName: isDivision && organization && organization.name,
    isActive: code === PROFILE_STATUS.ACTIVE,
    shortName: stageName && stageName.length ? stageName[0].toUpperCase() : '',
    statusCode: code,
    isSeekingRepresentation,
    seekingRepresentations,
    noteToRepresentative,
  };
}

export function extractOrganizationName(organization) {
  if (organization) {
    if (organization.name) {
      return organization.name;
    }

    if (organization.parentOrganization && organization.parentOrganization.name) {
      return organization.parentOrganization.name;
    }
  }

  return '';
}

export function sortArtists(artists) {
  return _sortBy(artists, [(a) => `${a.firstName} ${a.lastName}`.toLowerCase()], 'desc');
}

/**
 * Validates if a profile is actually within an account
 */
export const isProfileWithinArtist = (artists: UserContextFragment['artists'], profileId: string): boolean => {
  return !!artists.find((artist) => !!artist.profiles.find((profile) => profile.profileId === profileId));
};

export function getProfileFromSelectedArtist(selectedArtist, profileId) {
  if (selectedArtist && selectedArtist.profiles && !!selectedArtist.profiles.length) {
    return profileId
      ? selectedArtist.profiles.find((o) => o.profileId.toString() === profileId.toString())
      : selectedArtist.profiles[0];
  }

  return null;
}

// Get personal profile if exists in selected artists else return repped profile against passed profileId
export function getProfileInfoFromArtists<T extends gqlArtist | AccountArtistFragment>(
  artists: T[],
  selectedArtistId: number,
  profileId: string
) {
  const selectedArtist = getSelectedArtist(artists, selectedArtistId) as AccountArtistFragment;

  if (selectedArtist && selectedArtist.profiles && !!selectedArtist.profiles.length) {
    const personalProfile = selectedArtist.profiles.find(
      (o) => o.isPersonal === true && o.profileStatus.code === PROFILE_STATUS.ACTIVE
    );

    if (!_isEmpty(personalProfile)) {
      return personalProfile;
    }

    return selectedArtist.profiles.find((o) => o.profileId.toString() === profileId.toString());
  }

  return null;
}

export function truncateAndAddEllipsis(value: string, maxLength: number): string {
  if (value && maxLength && value.length > maxLength) {
    return `${value.substring(0, maxLength)}...`;
  }

  return value;
}

export const getDmas = (dmas: any[]): any[] => {
  const countries = _uniqBy(
    _map(dmas, (dma: any) => dma.region.country),
    'id'
  );
  const regions = _uniqBy(
    _map(dmas, (dma: any) => dma.region),
    'id'
  );

  return _map(countries, (country: any) => ({
    id: country.id,
    code: country.code,
    name: country.name,
    regions: _map(
      _filter(regions, (region: any) => region.country.id === country.id),
      (region: any) => ({
        id: region.id,
        name: region.name,
        code: region.code,
        dmas: _map(
          _filter(dmas, (dma: any) => dma.region.id === region.id),
          (dma: any) => ({
            id: dma.id,
            name: dma.name,
            code: dma.code,
          })
        ),
      })
    ),
  }));
};

/* ----  create mediaInput for attachMediaToProfile mutation  ---- */
export const createAttachMediaToProfileInput = (profileId, mediaId) => ({
  profileId: Number(profileId),
  isPrimary: false,
  mediaId: Number(mediaId),
  order: 0,
  mediaModerationStatusCode: MEDIA_MODERATION_STATUS.READY,
  paid: false,
  tag: '',
});

export const getMediaNameWithExtension = (media, maxLength?: number): string => {
  if (!media?.name) return '';

  const mediaName = maxLength ? media?.name.substr(0, maxLength) : media?.name;

  if (media?.fileType?.code) return `${mediaName}.${media.fileType.code.toLowerCase()}`;

  return mediaName;
};

export const getBillingItemCode = (mediaType) => {
  switch (mediaType) {
    case MEDIA_TYPE.AUDIO:
      return UPLOAD_ITEM.AUDIO_UPLOAD;
    case MEDIA_TYPE.VIDEO:
      return UPLOAD_ITEM.VIDEO_UPLOAD;
    default:
      return UPLOAD_ITEM.PHOTO_UPLOAD;
  }
};

export function getOldestProfile(artist) {
  if (artist?.profiles) {
    return artist.profiles
      .filter((o) => o.profileStatus.code === PROFILE_STATUS.ACTIVE)
      .map((o) => ({
        ...o,
        createdDate: new Date(o.created).getTime(),
      }))
      .sort((a, b) => a.createdDate - b.createdDate)[0];
  }

  return null;
}

export const isFromProfileEditPage = (forwardedPage): boolean => {
  const editPages = ['credits', 'skills-edit'];

  return editPages.includes(forwardedPage);
};

export const getSuccessMessagePersonalProfileCreated = () => {
  return {
    globalAlertFields: {
      text: 'Personal profile successfully created! Please try submitting again.',
      type: 'success',
    },
  };
};

enum HighestMembershipEnum {
  PREMIUM = 'premium',
  ESSENTIAL = 'essential',
  BASIC = 'basic',
}

enum PlanBillingCodeEnum {
  PREMIUM_ANNUAL = 'premium_annual',
  PREMIUM_M2M = 'premium_m2m',
  ESSENTIAL_ANNUAL = 'essential_annual',
  ESSENTIAL_M2M = 'essential_m2m',
  REGISTRATION = 'registration',
}

export type AccountSubscriptionStatusTypes =
  | HighestMembershipEnum.PREMIUM
  | HighestMembershipEnum.ESSENTIAL
  | HighestMembershipEnum.BASIC;

export const calculateAccountSubscriptionStatusType = (
  artists: AccountArtistFragment[]
): AccountSubscriptionStatusTypes => {
  return artists.reduce((highestMembership, artist) => {
    if (highestMembership === HighestMembershipEnum.PREMIUM) {
      return highestMembership;
    }

    const hasPremium =
      artist?.activeSubscription?.plan?.billingCode === PlanBillingCodeEnum.PREMIUM_ANNUAL ||
      artist?.activeSubscription?.plan?.billingCode === PlanBillingCodeEnum.PREMIUM_M2M ||
      artist?.previousSubscription?.plan?.billingCode === PlanBillingCodeEnum.PREMIUM_ANNUAL ||
      artist?.previousSubscription?.plan?.billingCode === PlanBillingCodeEnum.PREMIUM_M2M;

    if (hasPremium) {
      return HighestMembershipEnum.PREMIUM;
    }

    if (highestMembership === HighestMembershipEnum.ESSENTIAL) {
      return highestMembership;
    }

    const hasEssential =
      artist?.activeSubscription?.plan?.billingCode === PlanBillingCodeEnum.ESSENTIAL_ANNUAL ||
      artist?.activeSubscription?.plan?.billingCode === PlanBillingCodeEnum.ESSENTIAL_M2M ||
      artist?.previousSubscription?.plan?.billingCode === PlanBillingCodeEnum.ESSENTIAL_ANNUAL ||
      artist?.previousSubscription?.plan?.billingCode === PlanBillingCodeEnum.ESSENTIAL_M2M;

    if (hasEssential) {
      return HighestMembershipEnum.ESSENTIAL;
    }

    return highestMembership;
  }, HighestMembershipEnum.BASIC as HighestMembershipEnum);
};
